import type { FeatureProperties, JsonValue } from '../data/model';
import type { AttributeRule, AttributeRuleCondition } from './model';

function fieldValue(properties: FeatureProperties, field: string): JsonValue | undefined {
  return properties?.[field];
}

function isEmptyValue(value: JsonValue | undefined): boolean {
  return value === undefined || value === null || value === '';
}

function evaluateCondition(
  condition: AttributeRuleCondition,
  properties: FeatureProperties,
): boolean {
  const value = fieldValue(properties, condition.field);
  switch (condition.operator) {
    case 'is-empty':
      return isEmptyValue(value);
    case 'is-not-empty':
      return !isEmptyValue(value) && ['boolean', 'number', 'string'].includes(typeof value);
    case 'is-true':
      return value === true;
    case 'is-false':
      return value === false;
    case 'equals':
      return (
        (typeof value === 'number' &&
          Number.isFinite(value) &&
          typeof condition.value === 'number' &&
          value === condition.value) ||
        (typeof value === 'string' &&
          typeof condition.value === 'string' &&
          value === condition.value)
      );
    case 'does-not-equal':
      return (
        (typeof value === 'number' &&
          Number.isFinite(value) &&
          typeof condition.value === 'number' &&
          value !== condition.value) ||
        (typeof value === 'string' &&
          typeof condition.value === 'string' &&
          value !== condition.value)
      );
    case 'contains':
      return (
        typeof value === 'string' &&
        typeof condition.value === 'string' &&
        value.includes(condition.value)
      );
    case 'does-not-contain':
      return (
        typeof value === 'string' &&
        typeof condition.value === 'string' &&
        !value.includes(condition.value)
      );
    case 'greater-than':
      return (
        typeof value === 'number' &&
        Number.isFinite(value) &&
        typeof condition.value === 'number' &&
        value > condition.value
      );
    case 'greater-than-or-equal':
      return (
        typeof value === 'number' &&
        Number.isFinite(value) &&
        typeof condition.value === 'number' &&
        value >= condition.value
      );
    case 'less-than':
      return (
        typeof value === 'number' &&
        Number.isFinite(value) &&
        typeof condition.value === 'number' &&
        value < condition.value
      );
    case 'less-than-or-equal':
      return (
        typeof value === 'number' &&
        Number.isFinite(value) &&
        typeof condition.value === 'number' &&
        value <= condition.value
      );
  }
}

/** Null means there is no eligibility filter and therefore every feature matches. */
export function evaluateAttributeRule(
  rule: AttributeRule | null,
  properties: FeatureProperties,
): boolean {
  if (rule === null) return true;
  if (rule.kind === 'condition') return evaluateCondition(rule, properties);
  return rule.operator === 'and'
    ? rule.children.every((child) => evaluateAttributeRule(child, properties))
    : rule.children.some((child) => evaluateAttributeRule(child, properties));
}
